import { useContext, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { DateTime } from "luxon";
import { useCreateAuthToken, useHydrateAuth } from "../hooks/useAuth";
import { useGetSale } from "../hooks/useSale";
import { AuthContext, EntitiesContext } from "../App";
import logo from "../images/wespta-ski-logo-min.png";

const emailValidation =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

function Main() {
  const auth = useCreateAuthToken();
  const authenticated = useContext(AuthContext);
  const entities = useContext(EntitiesContext);
  const [email, setEmail] = useState("");
  const [valid, setValid] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState("");

  useGetSale();
  const sale = entities.state.get("sale").find((s) => s.open);
  const consignmentEndIso = sale?.consignment_end_iso;
  const consignmentEndDisplay = sale?.consignment_end;

  const onChange = (e) => setEmail(e.target.value);
  const validate = () => emailValidation.test(email.toLowerCase());

  const adminRoles = ["admin", "staff"];

  useHydrateAuth();

  useEffect(() => {
    if (!consignmentEndIso) {
      setTimeRemaining("");
      return;
    }

    const tenDaysMs = 10 * 24 * 60 * 60 * 1000;
    const targetDate = DateTime.fromISO(consignmentEndIso);

    const calculateTimeRemaining = () => {
      const difference = targetDate.toMillis() - DateTime.now().toMillis();

      if (difference <= 0) {
        setTimeRemaining("Consignment has closed");
        return "stop";
      }

      if (difference > tenDaysMs) {
        setTimeRemaining("");
        return "stop";
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      if (days > 0) {
        setTimeRemaining(`${days}d ${hours}h ${minutes}m ${seconds}s`);
      } else {
        setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
      }
      return "tick";
    };

    if (calculateTimeRemaining() !== "tick") return;

    const interval = setInterval(calculateTimeRemaining, 1000);
    return () => clearInterval(interval);
  }, [consignmentEndIso]);

  const reValidate = (e) => {
    if (!valid && validate()) {
      setValid(true);
    }
    if (e.key === "Enter") {
      auth.mutate({ email: email.toLowerCase() });
    }
  };

  if (authenticated.auth?.attributes?.role === "consigner")
    return <Navigate to="/dashboard" replace />;
  if (adminRoles.includes(authenticated.auth?.attributes?.role))
    return <Navigate to="/admin" replace />;

  return (
    <div className="hero is-medium">
      <div className="background" />
      <div className="hero-body">
        <div className="container has-text-centered">
          <img src={logo} width="75" />
          <h1 className="title has-text-grey-dark">Consignment Management</h1>
          <div className="subtitle">Consignment is now open for 2026</div>
          {/*<div className="subtitle">
            Consignment is closed for 2026.
            <br />
            You can still login to view the status of your inventory.
          </div>*/}
          {timeRemaining && timeRemaining !== "Consignment has closed" && (
            <div className="notification has-text-centered" style={{ maxWidth: '600px', margin: '0 auto 20px' }}>
              <p>Consignment closes in {timeRemaining} ({consignmentEndDisplay})</p>
            </div>
          )}
          {timeRemaining === "Consignment has closed" && (
            <div className="notification has-text-centered" style={{ maxWidth: '600px', margin: '0 auto 20px' }}>
              <p>Consignment has closed.<br/>You can still login to view the status of your inventory.</p>
            </div>
          )}
          <div className="columns">
            <div className="column is-one-third"></div>
            <div className="column is-one-third">
              {(auth.isSuccess || auth.isError) && (
                <div className="notification is-success">
                  An email has been sent with your login link.
                  <br />
                  Didn't receive an email? Check spam or contact{" "}
                  <a href="mailto:waitsfieldelementarypta@gmail.com">
                    waitsfieldelementarypta@gmail.com
                  </a>
                  {auth.isError && (
                    <div className="mt-2">
                      If you don't receive an email, you may need to{" "}
                      <Link to="/register">sign up</Link> or contact{" "}
                      <a href="mailto:waitsfieldelementarypta@gmail.com">
                        waitsfieldelementarypta@gmail.com
                      </a>
                    </div>
                  )}
                </div>
              )}
              <form name="login-email">
                {!valid && (
                  <div className="notification is-danger">
                    Invalid email address
                  </div>
                )}
                <div className="field has-addons">
                  <div className="control is-expanded">
                    <input
                      type="email"
                      autoFocus
                      name="email"
                      placeholder="Email"
                      className="input"
                      value={email}
                      onKeyPress={reValidate}
                      onBlur={onChange}
                      onChange={onChange}
                    />
                  </div>

                  <div className="control">
                    <a
                      onClick={() =>
                        auth.mutate({ email: email.toLowerCase() })
                      }
                      disabled={!valid}
                      className="button is-info"
                    >
                      Sign In
                    </a>
                  </div>
                </div>
              </form>
            </div>
            <div className="column is-on-third"></div>
          </div>
          <div className="has-text-centered">
            <p className="has-text-weight-semibold">
              <Link to="/register">
                <small>Don't have an account? Sign up</small>
              </Link>
            </p>
            <small>© 2026 waitsfieldpta.org</small>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Main;
